import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Fait défiler la page jusqu'à l'ancre de l'URL (`/#tarifs`, `/#realisations`…).
//
// React Router ne le fait PAS de lui-même : un <Link to="/#tarifs"> met bien
// l'ancre dans l'URL, mais rien ne bouge — et quand on est déjà sur "/", le
// chemin ne change même pas, donc le clic semble sans effet. C'est ce qui
// faisait que le menu (mobile comme bureau) ne menait nulle part.
//
// La difficulté : les sections de la page d'accueil arrivent d'un appel
// réseau (/theme/sections). Au premier rendu, l'élément visé n'existe pas
// encore — on réessaie donc pendant quelques secondes plutôt que d'abandonner
// au premier échec. Chaque tentative s'arrête net dès que l'élément est
// trouvé, et l'effet est annulé si l'on navigue ailleurs entre-temps.
const RETRY_MS = 100;
const MAX_TRIES = 40; // ~4 s, largement de quoi couvrir un chargement lent

export default function useHashScroll() {
  const location = useLocation();
  // `key` change à CHAQUE navigation, même vers la même URL : sans lui,
  // recliquer sur le même lien de menu ne relancerait pas le défilement.
  const { hash, pathname, key } = location;

  useEffect(() => {
    if (!hash || hash === "#") return undefined;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      id = hash.slice(1);
    }
    if (!id) return undefined;

    let tries = 0;
    let timer = 0;
    let cancelled = false;

    const jump = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        // Le décalage sous l'en-tête collant vient du `scroll-mt-*` porté par
        // chaque section, pas d'un calcul ici : c'est la section qui sait de
        // combien elle doit se décoller.
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
        return;
      }
      if (++tries < MAX_TRIES) timer = window.setTimeout(jump, RETRY_MS);
    };

    timer = window.setTimeout(jump, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hash, pathname, key]);
}
